import { computed, action, observable } from 'mobx';
import { models, utils } from '@kurtosys/ksys-app-template';
import { IIconProps } from '@kurtosys/ksys-app-components/dist/components/base/Icon/models';

import { StoreBase } from '../../../common/StoreBase';
import { ISearchCallToActionConfiguration, ISearchInputConfiguration } from '../models';
import React from 'react';
import { IClearSearchConfiguration } from '../models/IClearButtonConfiguration';
import { IErrorComponentStyles } from '../models/IErrorComponentStyles';
import { IComponentStyle } from '../../../models/commonTypes';

export class SearchBarStore extends StoreBase {
	static componentKey: 'searchBar' = 'searchBar';
	debounceTimeout: NodeJS.Timeout | undefined;
	ref = React.createRef<HTMLInputElement>();
	@observable searchTerm: string | undefined;
	@observable isLoading: boolean = false;

	@computed
	get callToActionConfiguration(): ISearchCallToActionConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration('callToAction');
		}
	}

	@computed
	get sanitizedSearchTerm(): string {
		return this.searchTerm || '';
	}

	/**
	 *
	 * @default true
	 * @readonly
	 */
	@computed
	get callToActionEnabled(): boolean {
		const defaultValue = true;
		if (this.callToActionConfiguration && this.callToActionConfiguration.enabled !== undefined) {
			return this.callToActionConfiguration.enabled;
		}
		return defaultValue;
	}

	/**
	 *
	 * @default true
	 * @readonly
	 */
	@computed
	get showCallToActionText(): boolean {
		const defaultValue = true;
		if (!this.callToActionIconProps) {
			// overrides showText boolean, as the button cannot be empty.
			return true;
		}
		if (this.callToActionConfiguration && this.callToActionConfiguration.showText !== undefined) {
			return this.callToActionConfiguration.showText;
		}
		return defaultValue;
	}

	@computed
	get callToActionText(): string | undefined {
		if (this.callToActionConfiguration) {
			return this.callToActionConfiguration.text;
		}
	}

	@computed
	get callToActionIconProps(): IIconProps | undefined {
		return this.callToActionConfiguration && this.callToActionConfiguration.icon;
	}

	@computed
	get inputConfiguration(): ISearchInputConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration('input');
		}
	}

	/**
	 *
	 * @default false
	 * @readonly
	 */
	@computed
	get showLabel(): boolean {
		const defaultValue = false;
		if (this.inputConfiguration) {
			return !!this.inputConfiguration.showLabel;
		}
		return defaultValue;
	}

	@computed
	get label(): string | undefined {
		if (this.inputConfiguration) {
			return this.inputConfiguration.label;
		}
	}

	@computed
	get placeholder(): string {
		const defaultValue = 'Search...';
		return (this.inputConfiguration && this.inputConfiguration.placeholder) || defaultValue;
	}

	@computed
	get minimumOptions() {
		return this.inputConfiguration && this.inputConfiguration.minimumOptions;
	}

	@observable
	showMinimumCharacterError: boolean = false;

	@computed
	get areMinimumCharactersMet(): boolean {
		return this.sanitizedSearchTerm.length >= this.minimumCharacters;
	}

	@computed
	get minimumCharacters(): number {
		const defaultValue = 3;
		if (this.minimumOptions && typeof this.minimumOptions.characterCount === 'number' && this.minimumOptions.characterCount >= 0) {
			return this.minimumOptions.characterCount;
		}
		return defaultValue;
	}

	@computed
	get minimumCharacterErrorMessage(): string {
		const defaultValue = 'Please provide {characterCount} character(s) or more and retry';
		const workingValue = (this.minimumOptions && this.minimumOptions.errorMessage) || defaultValue;
		return utils.replacePlaceholders({
			text: workingValue,
			values: {
				characterCount: this.minimumCharacters,
			},
		});
	}

	@computed
	get validateOnUserInput(): boolean {
		const defaultValue = false;
		if (this.minimumOptions) {
			return !!this.minimumOptions.validateOnUserInput;
		}
		return defaultValue;
	}

	@computed
	get debounceOptions() {
		return this.inputConfiguration && this.inputConfiguration.debounceOptions;
	}

	/**
	 *
	 * @default false
	 * @readonly
	 */
	@computed
	get debounceEnabled(): boolean {
		const defaultValue = false;
		if (this.debounceOptions) {
			return !!this.debounceOptions.enabled;
		}
		return defaultValue;
	}

	/**
	 *
	 * @default 500
	 * @readonly
	 */
	@computed
	get debounceTime(): number {
		return (this.debounceOptions && this.debounceOptions.time) || 500;
	}

	@computed
	get errorStyles(): IErrorComponentStyles | undefined {
		if (this.showMinimumCharacterError && this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration('errorStyles');
		}
	}

	@computed
	get callToActionErrorStyle(): IComponentStyle {
		const defaultValue = {};
		if (this.errorStyles) {
			return this.errorStyles.callToAction || defaultValue;
		}
		return defaultValue;
	}

	@computed
	get inputErrorStyle(): IComponentStyle {
		const defaultValue = {};
		if (this.errorStyles) {
			return this.errorStyles.input || defaultValue;
		}
		return defaultValue;
	}

	@computed
	get wrapperErrorStyle(): IComponentStyle {
		const defaultValue = {};
		if (this.errorStyles) {
			return this.errorStyles.wrapper || defaultValue;
		}
		return defaultValue;
	}

	@computed
	get clearButton(): IClearSearchConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration('clearSearch');
		}
	}

	@computed
	get clearIconProps(): IIconProps {
		if (this.clearButton && this.clearButton.icon) {
			return this.clearButton.icon;
		}
		return {
			asset: 'base.dialog.close',
		};
	}

	@computed
	get clearText(): string | undefined {
		if (this.clearButton && this.clearButton.text) {
			return this.clearButton.text;
		}
	}

	@computed
	get showClearText(): boolean {
		if (this.clearButton && this.clearButton.showText !== undefined) {
			return this.clearButton.showText;
		}
		return false;
	}

	@action
	handleClearSearch = () => {
		const { appStore, searchBarStore } = this.storeContext;
		if (this.clearButton) {
			searchBarStore.searchTerm = '';
			searchBarStore.triggerSearchEvent(false, true);
			appStore.clearResults(this.minimumCharacters > 0);
			searchBarStore.focus();
		}
	}

	@computed
	get showCallToAction(): boolean {
		// if nothing is enabled to fire off search results, then show the button.
		if (!this.callToActionEnabled && !this.debounceEnabled) {
			return true;
		}
		// otherwise only show the button if debounce is enabled, as the results will show automatically ~ no point to the button.
		return this.callToActionEnabled && !this.debounceEnabled;
	}

	@action
	async initialize(): Promise<void> {
		const searchTerm = this.storeContext.appStore.getInput('searchTerm');
		if (searchTerm) {
			this.searchTerm = searchTerm;
			await this.triggerSearchEvent();
		}
	}

	@action
	triggerSearchEvent = async (triggerInternalResults: boolean = true, showError: boolean = true) => {
		const { appStore, eventBusStore, resultsStore } = this.storeContext;
		if (this.areMinimumCharactersMet) {
			this.showMinimumCharacterError = false;
			if (triggerInternalResults) {
				this.isLoading = true;
				if (this.sanitizedSearchTerm.length > 0) {
					await appStore.fetchResults(this.sanitizedSearchTerm);
				}
				else {
					appStore.clearResults(false);
				}
				this.isLoading = false;
			}
			eventBusStore.trigger<models.eventBus.IGlobalSearchChangeDetail>(models.eventBus.EventType.globalSearchChange, {
				detail: {
					searchTerm: this.sanitizedSearchTerm,
				},
			});
		}
		else {
			this.showMinimumCharacterError = showError;
			appStore.clearResults(false);
			this.focus();
		}
		if (this.searchTerm === '' && this.minimumCharacters == 0) {
			appStore.addAlertMessage(resultsStore.noSearchTermPlaceholder);
		}
	}

	@action
	onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const searchTerm = e.target.value;
		this.changeSearchTerm(searchTerm);
	}

	@action
	onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.keyCode === 13) {
			e.preventDefault();
			this.triggerSearchEvent();
		}
		else if (e.keyCode === 27) {
			const { appStore } = this.storeContext;
			if (appStore) {
				appStore.clearResults();
			}
		}
	}

	@action
	changeSearchTerm = (searchTerm: string | undefined) => {
		this.searchTerm = searchTerm;
		if (this.validateOnUserInput) {
			this.showMinimumCharacterError = !this.areMinimumCharactersMet;
		}
		// debounce
		if (this.debounceEnabled) {
			if (this.debounceTimeout) {
				clearTimeout(this.debounceTimeout);
				this.debounceTimeout = undefined;
			}
			this.debounceTimeout = setTimeout(() => {
				this.triggerSearchEvent();
			}, this.debounceTime);
		}
	}

	focus = () => {
		if (this.ref && this.ref.current) {
			this.ref.current.focus();
		}
	}
}
